/**
 * ServingUnitCombobox — the shared serving-unit picker.
 *
 * Serving units are global and DB-backed (nutritionAPI.servingUnits); a unit
 * the user searches for and cannot find is created inline against that same
 * global list. Used wherever a form has to name a unit — the recipe servings
 * table and the per-serving supplement form both pick from here rather than
 * hand-rolling a third dropdown over the same endpoint.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { nutritionAPI } from "@/lib/nutrition-api";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";

interface ServingUnitComboboxProps {
  valueCode: number | null;
  valueName: string;
  onSelect: (code: number, name: string) => void;
  placeholder?: string;
}

/**
 * Searchable serving-unit picker over the global DB list, with inline create
 * for units that don't exist yet. Shared by the serving-type field on the form
 * and the add-extra-unit dialog below.
 */
export const ServingUnitCombobox = ({
  valueCode,
  valueName,
  onSelect,
  placeholder = "Select serving unit…",
}: ServingUnitComboboxProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: servingUnits = [], isLoading } = useQuery({
    queryKey: queryKeys.nutrition.servingUnits.list({ ordering: "name" }),
    queryFn: () => nutritionAPI.servingUnits.list({ ordering: "name" }),
    retry: false,
  });

  const createUnit = useMutation({
    mutationFn: (name: string) => nutritionAPI.servingUnits.create({ name }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.nutrition.servingUnits.list({ ordering: "name" }),
      });
      onSelect(created.code, created.name);
      setOpen(false);
      setSearch("");
      toast({
        title: "Serving unit created",
        description: `“${created.name}” added to the global list.`,
      });
    },
    onError: (err: any) => {
      toast({
        title: "Could not create serving unit",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return servingUnits;
    return servingUnits.filter((u) => u.name.toLowerCase().includes(q));
  }, [servingUnits, search]);

  const exactMatch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return servingUnits.find((u) => u.name.toLowerCase() === q) || null;
  }, [servingUnits, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn(
            "w-full justify-between",
            !valueCode && "text-muted-foreground"
          )}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading serving units…
            </span>
          ) : valueCode ? (
            valueName
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search serving units..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {filtered.length === 0 && !search.trim() && (
              <CommandEmpty>No serving units yet.</CommandEmpty>
            )}
            <CommandGroup>
              {filtered.map((unit) => (
                <CommandItem
                  key={unit.code}
                  value={unit.name}
                  onSelect={() => {
                    onSelect(unit.code, unit.name);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      valueCode === unit.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {unit.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {search.trim() && !exactMatch && (
              <CommandGroup heading="Not in the list?">
                <CommandItem
                  value={`__create__${search}`}
                  onSelect={() => createUnit.mutate(search.trim())}
                  disabled={createUnit.isPending}
                  className="text-primary"
                >
                  {createUnit.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  Create “{search.trim()}”
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ServingUnitCombobox;
